// src/pages/Login.jsx — FINAL (Forgot Password self-service ready, no double toasts/navigate)
import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext.jsx';
import Button from '../components/Button.jsx';
import Input from '../components/Input.jsx';
import LogoMark from '../components/LogoMark.jsx';
import Modal from '../components/Modal.jsx';
import { api } from '../lib/api.js';
import { toast } from 'react-hot-toast';

const isEmail = (v = '') => /\S+@\S+\.\S+/.test(String(v).trim());

function Login() {
  const { login, signup } = useAuth();

  const [isSignUp, setIsSignUp] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});

  // Forgot Password modal states
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotValue, setForgotValue] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  // ✅ NEW: store reset link result (for self-service)
  const [forgotResultUrl, setForgotResultUrl] = useState('');

  const forgotLabel = useMemo(() => {
    const v = forgotValue.trim();
    if (!v) return 'Email or Username';
    return isEmail(v) ? 'Email' : 'Username';
  }, [forgotValue]);

  const stars = useMemo(
    () => [
      { id: 1, top: '4%', left: '8%', size: 6, delay: 0.2, duration: 3.1 },
      { id: 2, top: '6%', left: '22%', size: 5, delay: 0.6, duration: 3.4 },
      { id: 3, top: '8%', left: '38%', size: 6, delay: 1.1, duration: 3.0 },
      { id: 4, top: '10%', left: '54%', size: 5, delay: 0.4, duration: 3.6 },
      { id: 5, top: '12%', left: '72%', size: 6, delay: 1.6, duration: 3.2 },
      { id: 6, top: '14%', left: '88%', size: 5, delay: 0.8, duration: 3.5 },
      { id: 7, top: '18%', left: '14%', size: 6, delay: 1.9, duration: 3.0 },
      { id: 8, top: '20%', left: '30%', size: 5, delay: 0.1, duration: 3.7 },
      { id: 9, top: '22%', left: '46%', size: 6, delay: 1.3, duration: 3.2 },
      { id: 10, top: '24%', left: '62%', size: 5, delay: 0.9, duration: 3.0 },
      { id: 11, top: '26%', left: '78%', size: 6, delay: 1.7, duration: 3.4 },
      { id: 12, top: '28%', left: '92%', size: 5, delay: 0.3, duration: 3.1 },
      { id: 13, top: '34%', left: '10%', size: 6, delay: 1.2, duration: 3.6 },
      { id: 14, top: '36%', left: '26%', size: 5, delay: 0.7, duration: 3.2 },
      { id: 15, top: '38%', left: '42%', size: 6, delay: 1.8, duration: 3.4 },
      { id: 16, top: '40%', left: '58%', size: 5, delay: 0.5, duration: 3.0 },
      { id: 17, top: '42%', left: '74%', size: 6, delay: 1.4, duration: 3.6 },
      { id: 18, top: '44%', left: '90%', size: 5, delay: 0.2, duration: 3.3 },
      { id: 19, top: '52%', left: '16%', size: 6, delay: 1.6, duration: 3.1 },
      { id: 20, top: '54%', left: '32%', size: 5, delay: 0.9, duration: 3.5 },
      { id: 21, top: '56%', left: '48%', size: 6, delay: 1.1, duration: 3.2 },
      { id: 22, top: '58%', left: '64%', size: 5, delay: 0.4, duration: 3.0 },
      { id: 23, top: '60%', left: '80%', size: 6, delay: 1.9, duration: 3.6 },
      { id: 24, top: '62%', left: '94%', size: 5, delay: 0.6, duration: 3.2 },
      { id: 25, top: '70%', left: '12%', size: 6, delay: 1.2, duration: 3.4 },
      { id: 26, top: '72%', left: '28%', size: 5, delay: 0.8, duration: 3.1 },
      { id: 27, top: '74%', left: '44%', size: 6, delay: 1.5, duration: 3.6 },
      { id: 28, top: '76%', left: '66%', size: 5, delay: 0.3, duration: 3.2 },
      { id: 29, top: '78%', left: '84%', size: 6, delay: 1.7, duration: 3.4 },
      { id: 30, top: '82%', left: '52%', size: 5, delay: 0.5, duration: 3.1 },
    ],
    [],
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.username.trim()) newErrors.username = 'Username is required';

    if (isSignUp) {
      if (!formData.email.trim()) newErrors.email = 'Email is required';
      if (formData.email && !isEmail(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
      if (!formData.password) newErrors.password = 'Password is required';
      if (formData.password && formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    } else {
      if (!formData.password) newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      if (isSignUp) {
        const res = await signup({
          username: formData.username.trim(),
          email: formData.email.trim(),
          password: formData.password,
        });

        // AuthContext sudah handle toast + navigate bila autoLogin
        if (res?.success && res?.autoLogin) return;

        if (res?.success && !res?.autoLogin) {
          setIsSignUp(false);
          setFormData({
            username: formData.username,
            email: '',
            password: '',
            confirmPassword: '',
          });
          return;
        }
        return;
      }

      // ==== Sign In ====
      await login(formData.username.trim(), formData.password);
      // AuthContext sudah handle toast + navigate
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setIsSignUp((prev) => !prev);
    setFormData({ username: '', email: '', password: '', confirmPassword: '' });
    setErrors({});
  };

  const openForgot = () => {
    setForgotResultUrl('');
    setForgotValue(formData.username?.trim() || '');
    setForgotOpen(true);
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Link copied!');
    } catch {
      // fallback
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      toast.success('Link copied!');
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    const v = forgotValue.trim();
    if (!v) {
      toast.error('Please enter your email or username');
      return;
    }

    setForgotLoading(true);
    setForgotResultUrl('');
    try {
      const payload = isEmail(v) ? { email: v } : { username: v };
      const { data } = await api.post('/auth/forgot-password', payload);

      // Jangan bocorkan apakah akun ada/tidak → selalu sukses
      toast.success('If the account exists, reset instructions have been sent.');

      // ✅ Self-service: backend bisa balikin reset_url atau token (dev mode)
      let url = '';

      if (data?.reset_url) {
        url = String(data.reset_url);
      } else if (data?.token) {
        // build URL from token
        const base = window.location.origin;
        url = `${base}/reset-password?token=${encodeURIComponent(String(data.token))}`;
      }

      // normalize relative url ("/reset-password?...") -> absolute
      if (url && url.startsWith('/')) {
        url = `${window.location.origin}${url}`;
      }

      // kalau ada url, tampilkan di modal (biar user bisa klik)
      if (url) {
        setForgotResultUrl(url);
        return;
      }

      // fallback (production biasanya kirim via email)
      // tetap biarkan modal terbuka supaya user paham "cek email"
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to send reset instructions');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated background accents */}
      <div className="pointer-events-none absolute inset-0">
        <motion.div
          className="absolute inset-0 opacity-50"
          style={{
            backgroundImage:
              'radial-gradient(1px 1px at 14px 22px, rgba(255,255,255,0.8), transparent 40%), radial-gradient(1px 1px at 42px 68px, rgba(255,255,255,0.6), transparent 40%), radial-gradient(1px 1px at 78px 28px, rgba(255,255,255,0.7), transparent 40%), radial-gradient(1px 1px at 96px 84px, rgba(255,255,255,0.5), transparent 40%), radial-gradient(1px 1px at 120px 54px, rgba(255,255,255,0.6), transparent 40%)',
            backgroundSize: '90px 90px',
          }}
          animate={{ y: [0, -40, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: 'linear' }}
        />
        {stars.map((star) => (
          <motion.span
            key={star.id}
            className="absolute bg-white"
            style={{
              top: star.top,
              left: star.left,
              width: `${star.size}px`,
              height: `${star.size}px`,
              boxShadow: '0 0 6px rgba(255,255,255,0.8)',
            }}
            animate={{ opacity: [0.2, 1, 0.2], scale: [1, 1.2, 1], x: [0, 12, 0], y: [0, -8, 0] }}
            transition={{
              duration: star.duration,
              delay: star.delay,
              repeat: Infinity,
              repeatType: 'mirror',
            }}
          />
        ))}
        <motion.div
          className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-emerald-400/15 blur-3xl"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1, x: [0, 20, 0], y: [0, -10, 0] }}
          transition={{ duration: 10, repeat: Infinity, repeatType: 'mirror' }}
        />
        <motion.div
          className="absolute top-10 -right-24 h-80 w-80 rounded-full bg-sky-400/15 blur-3xl"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1, x: [0, -16, 0], y: [0, 12, 0] }}
          transition={{ duration: 12, repeat: Infinity, repeatType: 'mirror' }}
        />
        <motion.div
          className="absolute -bottom-24 left-1/3 h-72 w-72 rounded-full bg-purple-400/10 blur-3xl"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1, x: [0, 10, 0], y: [0, -14, 0] }}
          transition={{ duration: 11, repeat: Infinity, repeatType: 'mirror' }}
        />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        {/* Brand */}
        <div className="text-center mb-1">
          <div className="mx-auto mb-0 inline-flex items-center justify-center">
            <LogoMark className="w-44 h-44" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-0 -mt-2">Spacebook</h1>
          <p className="text-gray-300">Modern workspace booking system</p>
        </div>

        {/* Auth Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.25 }}
          className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-2xl p-8 shadow-2xl"
        >
          <div className="flex mb-6">
            <button
              onClick={() => setIsSignUp(false)}
              className={`flex-1 py-2 text-center rounded-lg transition-all duration-200 ${
                !isSignUp
                  ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => setIsSignUp(true)}
              className={`flex-1 py-2 text-center rounded-lg transition-all duration-200 ${
                isSignUp
                  ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Sign Up
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <AnimatePresence mode="wait">
              <motion.div
                key={isSignUp ? 'signup' : 'signin'}
                initial={{ opacity: 0, x: isSignUp ? 16 : -16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: isSignUp ? -16 : 16 }}
                transition={{ duration: 0.2 }}
                className="space-y-4"
              >
                <Input
                  name="username"
                  label="Username"
                  value={formData.username}
                  onChange={handleInputChange}
                  error={errors.username}
                  placeholder="Enter your username"
                />

                {isSignUp && (
                  <Input
                    name="email"
                    label="Email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    error={errors.email}
                    placeholder="Enter your email"
                  />
                )}

                <Input
                  name="password"
                  label="Password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  error={errors.password}
                  placeholder="Enter your password"
                />

                {isSignUp && (
                  <Input
                    name="confirmPassword"
                    label="Confirm Password"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    error={errors.confirmPassword}
                    placeholder="Confirm your password"
                  />
                )}
              </motion.div>
            </AnimatePresence>

            <Button type="submit" loading={loading} className="w-full mt-6" size="lg">
              {isSignUp ? 'Create Account' : 'Sign In'}
            </Button>
          </form>

          {!isSignUp && (
            <div className="mt-4 text-center">
              <button
                onClick={openForgot}
                className="text-sm text-purple-400 hover:text-purple-300 transition-colors"
              >
                Forgot your password?
              </button>
            </div>
          )}

          <div className="mt-6 text-center">
            <p className="text-gray-400 text-sm">
              {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
              <button
                onClick={toggleMode}
                className="text-purple-400 hover:text-purple-300 transition-colors font-medium"
              >
                {isSignUp ? 'Sign In' : 'Sign Up'}
              </button>
            </p>
          </div>
        </motion.div>

        {/* Footer credit */}
        <div className="mt-6 text-center text-sm text-gray-400">
          © {new Date().getFullYear()}{' '}
          <span className="font-semibold text-white">Spacebook</span> — Developed by IT Team.
        </div>

        {/* Forgot Password Modal */}
        <Modal
          isOpen={forgotOpen}
          onClose={() => setForgotOpen(false)}
          title="Reset Password"
          size="md"
        >
          <form onSubmit={submitForgot} className="space-y-4">
            <p className="text-gray-300 text-sm">
              Enter your <span className="font-semibold text-white">email</span> or{' '}
              <span className="font-semibold text-white">username</span>. We’ll send reset instructions if the account exists.
            </p>

            <Input
              label={forgotLabel}
              value={forgotValue}
              onChange={(e) => setForgotValue(e.target.value)}
              placeholder="e.g. arif or arif@24slides.com"
            />

            {/* ✅ NEW: show self-service link if backend returns token/reset_url */}
            {forgotResultUrl ? (
              <div className="bg-gray-900/40 border border-gray-700 rounded-lg p-3 space-y-2">
                <p className="text-sm text-gray-200">
                  Reset link is ready (dev/self-service):
                </p>
                <div className="text-xs text-gray-300 break-all bg-gray-800/60 border border-gray-700 rounded-md p-2">
                  {forgotResultUrl}
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    className="flex-1"
                    onClick={() => (window.location.href = forgotResultUrl)}
                  >
                    Open Reset Page
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    className="flex-1"
                    onClick={() => copyToClipboard(forgotResultUrl)}
                  >
                    Copy Link
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-xs text-gray-400">
                If email delivery is configured, you’ll receive the reset link via email.
                (For self-service without email, backend must return <code>reset_url</code> or <code>token</code>.)
              </p>
            )}

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="secondary"
                className="flex-1"
                onClick={() => setForgotOpen(false)}
              >
                Close
              </Button>
              <Button type="submit" loading={forgotLoading} className="flex-1">
                Send Reset Link
              </Button>
            </div>
          </form>
        </Modal>
      </motion.div>
    </div>
  );
}

export default Login;










